import { AppErrors } from '../../core/error/appError';
import type { Reservation } from '../../core/model/reservation';
import { failure, success, type AppResult } from '../../core/result/appResult';
import type { ApiService } from '../api/apiService';
import { cacheReservations, loadCachedReservations } from '../local/reservationCache';
import { reservationToDomain } from '../mappers';
import { execute } from './apiCall';
import type { NewReservation, ReservationChange, ReservationRepository } from './repositories';

/**
 * Reservas, con caché local de solo lectura.
 *
 * La caché no es una optimización: es lo que hace que el código de confirmación siga a mano en
 * la puerta del restaurante cuando no hay cobertura.
 */
export class DefaultReservationRepository implements ReservationRepository {
  private cache: Reservation[] = loadCachedReservations();

  constructor(private readonly api: ApiService) {}

  cached(): Reservation[] {
    return this.cache;
  }

  async create(request: NewReservation): Promise<AppResult<Reservation>> {
    const result = await execute(() =>
      this.api.createReservation(request.idempotencyKey, {
        restaurant_id: request.restaurantId,
        date: request.date,
        time: request.time,
        party_size: request.partySize,
        slot_token: request.slotToken,
        guest_name: request.guestName,
        guest_phone: request.guestPhone,
        guest_email: request.guestEmail,
        occasion: request.occasion,
        notes: request.notes,
      }),
    );
    if (!result.ok) return result;
    const reservation = reservationToDomain(result.data);
    if (!reservation) return failure(AppErrors.unknown('Reserva ilegible'));
    this.putInCache(reservation);
    return success(reservation);
  }

  async getAll(forceRefresh = false): Promise<AppResult<Reservation[]>> {
    const result = await execute(() => this.api.getReservations());
    if (!result.ok) {
      // Sin conexión se responde con lo guardado en lugar de un error: la lista de reservas es
      // justo lo que el usuario necesita consultar sin red.
      if (result.error.kind === 'OFFLINE' && !forceRefresh && this.cache.length > 0) {
        return success(this.cache);
      }
      return result;
    }
    const items = result.data.map(reservationToDomain).filter((r): r is Reservation => r !== null);
    this.cache = items;
    cacheReservations(items);
    return success(items);
  }

  async get(id: string): Promise<AppResult<Reservation>> {
    const result = await execute(() => this.api.getReservation(id));
    if (!result.ok) {
      const cached = this.cache.find((r) => r.id === id);
      return cached ? success(cached) : result;
    }
    const reservation = reservationToDomain(result.data);
    if (!reservation) return failure(AppErrors.notFound());
    this.putInCache(reservation);
    return success(reservation);
  }

  async update(id: string, request: ReservationChange): Promise<AppResult<Reservation>> {
    const result = await execute(() =>
      this.api.updateReservation(id, {
        date: request.date,
        time: request.time,
        party_size: request.partySize,
        slot_token: request.slotToken,
        occasion: request.occasion,
        notes: request.notes,
      }),
    );
    if (!result.ok) return result;
    const reservation = reservationToDomain(result.data);
    if (!reservation) return failure(AppErrors.unknown('Reserva ilegible'));
    this.putInCache(reservation);
    return success(reservation);
  }

  async cancel(id: string): Promise<AppResult<void>> {
    const result = await execute(() => this.api.cancelReservation(id));
    if (!result.ok) return result;
    // La reserva no se borra de la caché: se marca cancelada y sigue visible en el histórico.
    this.cache = this.cache.map((r) => (r.id === id ? { ...r, status: 'CANCELLED' as const } : r));
    cacheReservations(this.cache);
    return success(undefined);
  }

  private putInCache(reservation: Reservation): void {
    const index = this.cache.findIndex((r) => r.id === reservation.id);
    this.cache = index >= 0
      ? this.cache.map((r) => (r.id === reservation.id ? reservation : r))
      : [...this.cache, reservation];
    cacheReservations(this.cache);
  }
}
